import { ObjectUtils } from 'Helpers';

import { Logger } from '../../../../Errors/Logger';
import { I18n } from '../../../../Locales/I18nService';
import { RoomInfos } from '../../../../Web/Pages/Quote/Components/Pannels/Rooms/Controllers/RoomInfosValues';
import { MapContextState } from '../../Context/MapContext';
import { IContournements } from '../../Models/Contournements';
import { YesOrNo } from '../../Models/IMapItem';
import { IOpening, OpeningType } from '../../Models/IOpening';
import { IRoom, RoomLocalisation, RoomShape, RoomType, StairsSteps, StairsStepType } from '../../Models/IRoom';
import { IRoomItem, RoomItemType } from '../../Models/IRoomItem';
import { OpeningUtils } from '../../Utils/Opening/OpeningUtils';
import { PointUtils } from '../../Utils/PointUtils';
import { RoomUtils } from '../../Utils/Room/RoomUtils';
import { CoordPoint } from '../../Utils/Types';
import { MapHelper } from '../MapHelper';
import { fixRoomProperties } from './MapExportFixChanges';
import { ApiMapState, ExportMap } from './MapExportHelper';
import { LameFlooringLayout } from 'Plugins/FloorPlan/Components/Laizes/Laize';

type ExportMapV33Data = {
    rooms?: Partial<IExportRoom>[];
    openings?: Partial<IExportOpeningItem>[];
    roomItems?: Partial<IExportRoomItem>[];
    lockedWallsList?: IExportLockedWall[];
};

type ExportMapV33 = ExportMap & { map?: ExportMapV33Data };

export class MapExportV33 {
    public static VERSION = 3.3;

    public static toApiJson = (state: Partial<MapContextState>) => {
        const { openings, rooms, roomItems } = MapHelper.getRoomAllElements(state.rooms);
        const exportOpenings = OpeningUtils.exportOpenings(openings, rooms);
        const exportRooms = state.rooms?.map(RoomUtils.exportRoom);
        const result: ExportMapV33 = {
            version: MapExportV33.VERSION,
            map: {
                rooms: exportRooms?.map(this.toExportRoom),
                openings: exportOpenings.map(this.toExportOpeningItem),
                roomItems: roomItems?.map(this.toExportRoomItem),
                lockedWallsList: state.lockedWallsList,
            },
        };
        const json = JSON.stringify(result);
        return json;
    };

    public static fromApiJsonToState = ({ map }: ExportMapV33, roomInfos: RoomInfos): ApiMapState => {
        const {
            rooms: exportRooms = [],
            openings: exportOpenings = [],
            roomItems: exportRoomItems = [],
            lockedWallsList,
        } = map || {};

        const rooms: IRoom[] = exportRooms.map((exportRoom: Partial<IExportRoom>) => {
            const room: IRoom = this.toRoom(exportRoom, roomInfos);
            room.openings = exportOpenings
                .filter(
                    (o: Partial<IExportOpeningItem>, index: number) =>
                        exportOpenings?.findLastIndex((x) => x.openingId === o.openingId) === index
                ) // filter duplicates
                .filter((o: Partial<IExportOpeningItem>) => o.roomId === room.roomId)
                .map(this.toOpeningItem);
            room.roomItems = exportRoomItems
                .filter(
                    (o: Partial<IExportRoomItem>, index: number) =>
                        exportRoomItems?.findLastIndex((x) => x.roomItemId === o.roomItemId) === index
                ) // filter duplicates
                .filter((i: Partial<IExportRoomItem>) => i.roomId === room.roomId)
                .map(this.toRoomItem);

            return room;
        });

        const exportMap = {
            state: { rooms },
            lockedWallsList,
            flooringWallStartIndexes: exportRooms.reduce<Record<string, number>>((acc, room) => {
                if (room.roomId && room.flooringWallStartIndex !== undefined) {
                    acc[room.roomId] = room.flooringWallStartIndex;
                }
                return acc;
            }, {}),
        };
        Logger.log('MapExportV3.3 : fromApiJsonToState', exportMap);
        return exportMap;
    };

    //#region //* CONVERTER : MAP_ITEM TO EXPORT_ITEM

    public static toExportRoom = (baseRoom: IRoom): Partial<IExportRoom> => {
        const room = RoomUtils.exportRoom(baseRoom);
        return {
            roomId: room.roomId,
            name: room.name,
            namedByUser: room.namedByUser,
            shape: room.shape,
            type: room.type,
            roomWall: room.roomWall,

            localisation: room.localisation,
            roomType: room.roomType,
            roomFloor: room.roomFloor,
            roomFloorDescription: room.roomFloorDescription,
            roomHeating: room.roomHeating,
            roomHeatingType: room.roomHeatingType,
            roomExistingFlooring: room.roomExistingFlooring,
            roomExistingFloorType: room.roomExistingFloorType,

            //* Stairs
            stairsSteps: room.stairsSteps?.map(this.toExportStairsSteps),
            hasContremarches: room.hasContremarches,

            questionsCopiedFromId: room.questionsCopiedFromId,

            contournements: room.contournements,
            flooringWallStartValidated: room.flooringWallStartValidated,
            flooringWallStartIndex: room.flooringWallStart ? room.walls.indexOf(room.flooringWallStart) : undefined,
            lameFlooringLayout: room.lameFlooringLayout,

            //* COORDS
            coords: room.coords,
            center: room.center,
        };
    };

    public static toExportOpeningItem = (opening: IOpening): Partial<IExportOpeningItem> => {
        return {
            openingId: opening.openingId,
            roomId: opening.roomId,
            type: opening.type,
            name: opening.name,
            namedByUser: opening.namedByUser,

            size: opening.size,
            customSize: opening.customSize,

            location_side: opening.location_side,
            location_position_from_edge: opening.location_position_from_edge,

            x: opening.x,
            y: opening.y,
            limit: opening.limit, //! WHY
            angle: opening.angle, //! WHY
        };
    };

    public static toExportRoomItem = (roomItem: IRoomItem): Partial<IExportRoomItem> => {
        return {
            type: roomItem.type,
            roomItemId: roomItem.roomItemId,
            roomId: roomItem.roomId,
            name: roomItem.name,
            namedByUser: roomItem.namedByUser,
            dimensId: roomItem.dimensId,

            putFlooring: roomItem.putFlooring,
            putPlinthes: roomItem.putPlinthes,
            isMoveableOutside: roomItem.isMoveableOutside,

            coordsReal: roomItem.coordsReal,
            graphTranslation: PointUtils.toPosition(roomItem.graphTranslation as CoordPoint),
        };
    };

    public static toExportStairsSteps = (step: StairsSteps): IExportStairsSteps => {
        return {
            id: step.id,
            name: step.name,
            type: step.type,
            marches: step.marches,
            giron: step.giron,
            contremarches: step.contremarches,
            quantity: step.quantity,
        };
    };

    //#endregion

    //#region //* CONVERTER : EXPORT_ITEM TO MAP_ITEM

    public static toRoom = (room: IExportRoom, roomInfos: RoomInfos): IRoom => {
        //const room = CreateRoomFactory.exportRoom(baseRoom);
        fixRoomProperties(room);
        if (room.roomHeatingType) {
            room.roomHeating =
                room.roomHeatingType === I18n.get('RoomHeatingTypeList_PasDeChauffage') ? YesOrNo.No : YesOrNo.Yes;
        }
        if (room.roomExistingFloorType) {
            const flooringTypeList =
                room.roomExistingFlooring === YesOrNo.Yes
                    ? roomInfos.getCurrentFloorWithFlooring(room.localisation!, room.type!)
                    : roomInfos.getCurrentFloorWithoutFlooring(room.localisation!, room.roomType!, room.type!);

            if (
                flooringTypeList.every((x) =>
                    x.value ? x.value !== room.roomExistingFloorType : x.label !== room.roomExistingFloorType
                )
            ) {
                room.roomExistingFloorType = undefined;
            }
        }
        if (!room.type) {
            room.type = RoomType.Sol;
        }
        const coords = room.coords;
        const roomCoordsMerged = coords ? ObjectUtils.ignoreDuplicates(coords.slice(0, coords.length - 1)) : undefined;

        console.log('passe iciiiii');
        return {
            roomId: room.roomId,
            name: room.name,
            namedByUser: room.namedByUser,
            shape: room.shape,
            type: room.type,
            localisation: room.localisation,
            roomType: room.roomType,
            roomWall: room.roomWall,
            roomFloor: room.roomFloor,
            roomFloorDescription: room.roomFloorDescription,
            roomHeating: room.roomHeating,
            roomHeatingType: room.roomHeatingType,
            roomExistingFlooring: room.roomExistingFlooring,
            roomExistingFloorType: room.roomExistingFloorType,

            questionsCopiedFromId: room.questionsCopiedFromId,

            contournements: room.contournements,
            flooringWallStartValidated: room.flooringWallStartValidated,

            hasContremarches: room.hasContremarches,
            stairsSteps: room.stairsSteps?.map(this.toStairsSteps),
            //* Set obsolete values to undefined
            lameFlooringLayout: Object.values(LameFlooringLayout).includes(
                room.lameFlooringLayout as LameFlooringLayout
            )
                ? room.lameFlooringLayout
                : undefined,

            //* COORDS
            center: room.center,
            coords: roomCoordsMerged ? [...roomCoordsMerged, roomCoordsMerged[0]] : undefined,

            walls: [],
            openings: [],
            roomItems: [],
        };
    };

    public static toOpeningItem = (opening: IExportOpeningItem): IOpening => {
        return {
            openingId: opening.openingId,
            roomId: opening.roomId,

            type: opening.type,
            name: opening.name,
            size: opening.size,
            customSize: opening.customSize,

            location_side: opening.location_side,
            location_position_from_edge: opening.location_position_from_edge,

            x: opening.x,
            y: opening.y,
            limit: opening.limit,
            angle: opening.angle,

            namedByUser: opening.namedByUser,
        };
    };

    public static toRoomItem = (roomItem: Partial<IExportRoomItem>): IRoomItem => {
        if (roomItem.dimensId === undefined) {
            roomItem.dimensId = 0;
        }
        return {
            type: roomItem.type,
            roomItemId: roomItem.roomItemId,
            roomId: roomItem.roomId,
            name: roomItem.name,
            namedByUser: roomItem.namedByUser,
            dimensId: roomItem.dimensId,

            putFlooring: roomItem.putFlooring,
            putPlinthes: roomItem.putPlinthes,
            isMoveableOutside: roomItem.isMoveableOutside,

            coordsReal: roomItem.coordsReal,
            graphTranslation: roomItem.graphTranslation,
        };
    };

    public static toStairsSteps = (step: IExportStairsSteps): StairsSteps => {
        return {
            id: step.id,
            name: step.name,
            type: step.type,
            marches: step.marches,
            giron: step.giron,
            contremarches: step.contremarches,
            quantity: step.quantity,
        };
    };

    //#endregion
}

//#region //* EXPORT MODELS

interface IExportRoom {
    shape?: RoomShape;
    roomId?: string;
    name?: string;
    namedByUser?: boolean;

    //* ROOM INFOS : injected in context
    roomType?: string; // rename
    localisation?: RoomLocalisation;
    roomFloor?: string;
    roomFloorDescription?: string;
    roomExistingFlooring?: YesOrNo | string; //presenceRevetementAuSol
    roomExistingFloorType?: string;
    roomHeating?: YesOrNo | string;
    roomHeatingType?: string;
    type?: RoomType;
    roomWall?: string;

    questionsCopiedFromId?: string;

    contournements?: IContournements;
    stairsSteps?: Array<IExportStairsSteps>;
    hasContremarches?: boolean;

    flooringWallStartValidated?: boolean;
    flooringWallStartIndex?: number;
    lameFlooringLayout?: LameFlooringLayout;

    //* injected by MapResult -> calculate
    // raccord_length?: number;
    // room_length?: number;
    // surface_area?: number;
    // surface_area_without_room_item?: number;
    // surface_manipulee_prorata?: number;
    // perimeter?: number;
    // perimeter_plinthes?: number;
    // should_lay_plinthes_inside_room_element?: boolean;

    center?: CoordPoint;
    coords?: Array<CoordPoint>;
}

interface IExportOpeningItem {
    openingId?: string;
    roomId?: string;

    type?: OpeningType;
    name?: string;
    namedByUser?: boolean;

    size?: number;
    customSize?: boolean;

    //* OpeningProperties
    doorType?: string;
    unhingeableOpening?: string; //* Oui ou Non

    //* COORDS
    x?: number;
    y?: number;
    limit?: CoordPoint[];
    angle?: number;
    location_side?: string;
    location_position_from_edge?: number;
}

interface IExportRoomItem {
    roomItemId?: string;
    roomId?: string;

    name?: string;
    namedByUser?: boolean;

    type: RoomItemType;
    putFlooring?: YesOrNo;
    putPlinthes?: string;
    isMoveableOutside?: boolean; // #20350
    dimensId?: number;

    width?: number;
    height?: number;

    coords?: Array<CoordPoint>;
    coordsReal?: Array<CoordPoint>;
    position?: CoordPoint;
    graphTranslation?: CoordPoint;

    angle?: number;
}

export interface IExportLockedWall {
    roomId: string;
    wallIndex: number;
}

export interface IExportStairsSteps {
    id: string;
    name: string;
    type: StairsStepType;
    marches: number; //Emmarchement
    giron: number; //Profondeur de marche
    hasContremarches?: boolean;
    contremarches?: number;
    quantity: number;
}

//#endregion
